/**
 * Estabelecimentos Table Component
 */

import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Eye, Pencil, Trash2, Plus, Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Estabelecimento } from "@/types/estabelecimento";
import { can } from "@/lib/rbac";
import { useSession } from "@/state/session";

interface EstabelecimentosTableProps {
  estabelecimentos: Estabelecimento[];
  onView: (estabelecimento: Estabelecimento) => void;
  onEdit: (estabelecimento: Estabelecimento) => void;
  onDelete: (estabelecimento: Estabelecimento) => void;
  onCreate: () => void;
}

export function EstabelecimentosTable({
  estabelecimentos,
  onView,
  onEdit,
  onDelete,
  onCreate,
}: EstabelecimentosTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Estabelecimento;
    direction: "asc" | "desc";
  }>({ key: "nome", direction: "asc" });

  const { user } = useSession();
  const role = user?.role;

  const filteredEstabelecimentos = estabelecimentos.filter((est) =>
    est.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    est.cnpj?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    est.responsavel?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSort = (key: keyof Estabelecimento) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedEstabelecimentos = [...filteredEstabelecimentos].sort((a, b) => {
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const getSortIcon = (key: keyof Estabelecimento) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4" />;
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  const canCreate = can(role, "estabelecimentos", "create");
  const canEdit = can(role, "estabelecimentos", "edit");
  const canDelete = can(role, "estabelecimentos", "delete");

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar estabelecimentos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        {canCreate && (
          <Button onClick={onCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Estabelecimento
          </Button>
        )}
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="cursor-pointer select-none hover:bg-muted/50"
                onClick={() => handleSort("nome")}
              >
                <div className="flex items-center gap-2">
                  Nome {getSortIcon("nome")}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none hover:bg-muted/50"
                onClick={() => handleSort("cnpj")}
              >
                <div className="flex items-center gap-2">
                  CNPJ {getSortIcon("cnpj")}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none hover:bg-muted/50"
                onClick={() => handleSort("responsavel")}
              >
                <div className="flex items-center gap-2">
                  Responsável {getSortIcon("responsavel")}
                </div>
              </TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
          {sortedEstabelecimentos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  Nenhum estabelecimento encontrado
                </TableCell>
              </TableRow>
            ) : (
              sortedEstabelecimentos.map((estabelecimento) => (
                <TableRow key={estabelecimento.id_estabelecimento}>
                  <TableCell className="font-medium">{estabelecimento.nome}</TableCell>
                  <TableCell>{estabelecimento.cnpj || "-"}</TableCell>
                  <TableCell>{estabelecimento.responsavel || "-"}</TableCell>
                  <TableCell>{estabelecimento.telefone || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onView(estabelecimento)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(estabelecimento)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(estabelecimento)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </div>
    </div>
  );
}
